import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'astroturf/react';

import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { trackClick } from 'js/utils/trackClick';
import routes from 'js/core_pre_webpack/routes';
import { Button } from 'js/design_system/Button';
import { captureException } from '@sentry/browser';

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 10000;
`;

const OverlayMask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
`;

const CutoutOverlay = styled.div`
  position: absolute;
  background: transparent;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
`;

const TooltipBox = styled.div`
  position: absolute;
  background: white;
  border-radius: 8px;
  padding: 16px;
  max-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10001;
  pointer-events: auto;
`;

const TooltipArrow = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid white;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
`;

const TooltipArrowRight = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 8px solid white;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
`;

const TooltipBody = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 400;
  color: #141413;
  line-height: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #666;
  font-size: 16px;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const StepIndicator = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  color: #666;
  font-size: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

interface Props {
  onComplete: () => void;
}

const tourSteps = [
  {
    id: 'mentions',
    body: 'Track your citations, get realtime notifications and measure the impact of your work.',
    targetSelectors: [
      'a[href*="/mentions"]',
      'a[href*="/upgrade"][href*="feature=searchm"]',
    ],
    fallbackText: 'Mentions',
  },
  {
    id: 'analytics',
    body: 'See who is engaging with your work, reading your papers, and viewing your CV.',
    targetSelectors: [
      'a[href*="/analytics"]',
      'a[href*="analytics_dashboard"]',
      '[aria-label*="Analytics"]',
      'a[key="analytics"]',
    ],
    fallbackText: 'Analytics',
  },
  {
    id: 'tools',
    body: 'Access a variety of tools for built for academics.',
    targetSelectors: ['button[aria-pressed]', 'button[aria-label*="Tools"]'],
    fallbackText: 'Tools',
  },
  {
    id: 'pdf-packs',
    body: 'Accelerate your research with curated PDF packages of related papers connected by citations and similar topics.',
    targetSelectors: [
      '[data-testid="pdf-packs"]',
      'a[href*="pdf_packs"]',
      'button[aria-label*="PDF Packs"]',
    ],
    fallbackText: 'PDF Packs',
  },
];

export const OnboardingTour: React.FC<Props> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [hasError, setHasError] = useState(false);
  const isMountedRef = useRef(true);
  const tooltipBoxRef = useRef<HTMLDivElement>(null);

  const hideOnboardingTooltips = useCallback(() => {
    const route =
      routes.hide_onboarding_tooltips_api_v0_onboarding_overhaul_users_path({
        subdomain_param: 'api',
      });

    fetchWithCsrf(route, {
      keepalive: true,
    });
  }, []);

  const handleClose = useCallback(() => {
    trackClick({
      source: 'SocialHome.OnboardingTour',
      extra: {
        action: 'close',
        step: currentStep + 1,
      },
    });
    hideOnboardingTooltips();
    onComplete();
  }, [currentStep, onComplete, hideOnboardingTooltips]);

  const findTargetElement = useCallback((step: (typeof tourSteps)[0]) => {
    // Try the specific selectors first
    for (const selector of step.targetSelectors) {
      try {
        const element = document.querySelector(selector) as HTMLElement;
        if (element && element.offsetParent !== null) {
          return element;
        }
      } catch (_e) {
        // Skip invalid selectors
        continue;
      }
    }

    // Special handling for analytics - look for elements with Analytics text that are links
    if (step.id === 'analytics') {
      const allLinks = Array.from(document.querySelectorAll('a'));
      for (const link of allLinks) {
        if (
          (link.textContent?.includes('Analytics') ||
            link.getAttribute('href')?.includes('analytics')) &&
          (link as HTMLElement).offsetParent !== null
        ) {
          return link as HTMLElement;
        }
      }
    }

    // Special handling for PDF Packs in the tools sidebar
    if (step.id === 'pdf-packs') {
      // First try to find the ProminentLink containing PDF Packs
      const toolsLinks = Array.from(
        document.querySelectorAll('a[href*="research"]'),
      );
      for (const link of toolsLinks) {
        if (
          link.textContent?.includes('PDF Packs') &&
          (link as HTMLElement).offsetParent !== null
        ) {
          return link as HTMLElement;
        }
      }

      // Fallback: look for any clickable element with PDF Packs text
      const clickableElements = Array.from(
        document.querySelectorAll('a, button'),
      );
      for (const element of clickableElements) {
        if (
          element.textContent?.includes('PDF Packs') &&
          (element as HTMLElement).offsetParent !== null
        ) {
          return element as HTMLElement;
        }
      }
    }

    // Fallback: look for any element containing the fallback text
    const allElements = Array.from(
      document.querySelectorAll('a, button, div, span'),
    );
    for (const element of allElements) {
      if (
        element.textContent?.includes(step.fallbackText) &&
        (element as HTMLElement).offsetParent !== null
      ) {
        return element as HTMLElement;
      }
    }

    return null;
  }, []);

  const updateTargetPosition = useCallback(() => {
    try {
      if (!isMountedRef.current) return;

      const step = tourSteps[currentStep];
      if (!step) return;

      const element = findTargetElement(step);
      if (!isMountedRef.current) return;

      if (element) {
        const rect = element.getBoundingClientRect();
        if (isMountedRef.current) {
          setTargetRect(rect);
        }
      } else {
        // If we can't find the target element, use a fallback position
        if (isMountedRef.current) {
          setTargetRect(null);
        }
      }
    } catch (_error) {
      // console.warn('Error updating target position:', error);
      if (isMountedRef.current) {
        setHasError(true);
      }
    }
  }, [currentStep, findTargetElement]);

  useEffect(() => {
    isMountedRef.current = true;

    updateTargetPosition();

    const handleUpdate = () => {
      if (isMountedRef.current) {
        updateTargetPosition();
      }
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate);

    const timeout = setTimeout(() => {
      if (isMountedRef.current) {
        updateTargetPosition();
      }
    }, 100);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate);
      clearTimeout(timeout);
    };
  }, [updateTargetPosition]);

  useEffect(() => {
    if (currentStep === tourSteps.length - 1) {
      const handleDocumentClick = (e: MouseEvent) => {
        if (
          tooltipBoxRef.current &&
          !tooltipBoxRef.current.contains(e.target as Node)
        ) {
          handleClose();
        }
      };
      document.addEventListener('mousedown', handleDocumentClick);
      return () => {
        document.removeEventListener('mousedown', handleDocumentClick);
      };
    }
  }, [currentStep, handleClose]);

  const getTooltipPosition = () => {
    try {
      if (!targetRect)
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

      const tooltipWidth = 280;
      const tooltipHeight = 120;
      const margin = 16;
      const currentStepData = tourSteps[currentStep];

      if (!currentStepData) {
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
      }

      // Special positioning for PDF Packs in sidebar
      if (currentStepData?.id === 'pdf-packs') {
        // Position the tooltip so the arrow points to center of target
        const targetCenterY = targetRect.top + targetRect.height / 2;
        let top = targetCenterY - tooltipHeight / 2 - 18;
        let left = targetRect.left - tooltipWidth - margin;

        // If there's not enough space on the left, position on the right
        if (left < margin) {
          left = targetRect.right + margin;
        }

        // Ensure tooltip doesn't go off screen
        const minTop = margin;
        const maxTop = window.innerHeight - tooltipHeight - margin;

        if (top < minTop) {
          top = minTop;
        } else if (top > maxTop) {
          top = maxTop;
        }

        return {
          top: `${top}px`,
          left: `${left}px`,
        };
      }

      // Default positioning for other steps (below the target)
      let top = targetRect.bottom + margin;
      let left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;

      if (left < margin) {
        left = margin;
      } else if (left + tooltipWidth > window.innerWidth - margin) {
        left = window.innerWidth - tooltipWidth - margin;
      }

      if (top + tooltipHeight > window.innerHeight - margin) {
        top = targetRect.top - tooltipHeight - margin;
      }

      return {
        top: `${top}px`,
        left: `${left}px`,
      };
    } catch (_error) {
      // console.warn('Error calculating tooltip position:', error);
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
  };

  const handleNext = () => {
    try {
      if (!isMountedRef.current) return;

      trackClick({
        source: 'SocialHome.OnboardingTour',
        extra: {
          action: currentStep < tourSteps.length - 1 ? 'next' : 'finish',
          step: currentStep + 1,
        },
      });

      if (currentStep < tourSteps.length - 1) {
        // If moving to step 4 (pdf-packs), open the tools sidebar first, then advance step
        if (currentStep === 2) {
          // Going from step 3 (tools) to step 4 (pdf-packs)
          try {
            const toolsButton = document.querySelector(
              'button[aria-pressed]',
            ) as HTMLButtonElement;
            if (
              toolsButton &&
              toolsButton.getAttribute('aria-pressed') !== 'true'
            ) {
              toolsButton.click();
              // Wait for sidebar to open before advancing to next step
              setTimeout(() => {
                if (isMountedRef.current) {
                  setCurrentStep(currentStep + 1);
                }
              }, 200);
              return;
            }
          } catch (_error) {
            // console.warn('Error opening tools sidebar:', error);
          }
        }

        if (isMountedRef.current) {
          setCurrentStep(currentStep + 1);
        }
      } else {
        hideOnboardingTooltips();
        onComplete();
      }
    } catch (_error) {
      // console.warn('Error in handleNext:', error);
      if (isMountedRef.current) {
        setHasError(true);
      }
    }
  };

  // Early return if there's an error or invalid step
  if (hasError || currentStep < 0 || currentStep >= tourSteps.length) {
    return null;
  }

  const currentStepData = tourSteps[currentStep];
  if (!currentStepData) {
    return null;
  }

  const renderOverlayMask = () => {
    if (!targetRect) {
      return <OverlayMask />;
    }

    const margin = 8;
    const left = Math.max(0, targetRect.left - margin);
    const top = Math.max(0, targetRect.top - margin);
    const right = Math.min(window.innerWidth, targetRect.right + margin);
    const bottom = Math.min(window.innerHeight, targetRect.bottom + margin);

    return (
      <CutoutOverlay
        style={{
          top: `${top}px`,
          left: `${left}px`,
          width: `${right - left}px`,
          height: `${bottom - top}px`,
        }}
      />
    );
  };

  try {
    return (
      <OverlayContainer>
        {renderOverlayMask()}
        <TooltipBox ref={tooltipBoxRef} style={getTooltipPosition()}>
          {currentStepData.id === 'pdf-packs' ? (
            <TooltipArrowRight />
          ) : (
            <TooltipArrow />
          )}
          <CloseButton onClick={handleClose}>×</CloseButton>
          <TooltipBody>{currentStepData.body}</TooltipBody>
          <ButtonContainer>
            {currentStep < tourSteps.length - 1 ? (
              <Button iconRight="arrow_forward" size="xs" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button size="xs" onClick={handleNext}>
                Finish
              </Button>
            )}
          </ButtonContainer>
          <StepIndicator>{currentStep + 1} of 4</StepIndicator>
        </TooltipBox>
      </OverlayContainer>
    );
  } catch (_error) {
    captureException(_error);
    return null;
  }
};
